/**
 * AuthGuard — wraps the app and shows LoginPage if not authenticated.
 * On mount, attempts to restore a previous session from the stored refresh token.
 *
 * №14.1.1-C: the waiting state was part of the sixth orphan design system
 * (Tailwind + `#0a0a0f`); it now renders from the one component layer.
 */

import { useEffect, useState } from "react";
import { useAuth } from "../hooks/use-auth";
import { restoreSession } from "../auth";
import { LoginPage } from "./LoginPage";
import { AuthSpinner } from "./AuthSpinner";

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated } = useAuth();
  const [restoring, setRestoring] = useState(!isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) { setRestoring(false); return; }
    restoreSession().finally(() => setRestoring(false));
  }, []);

  if (restoring) {
    return <AuthSpinner label="Loading…" />;
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return <LoginPage />;
}
